import { z } from 'zod';
import {
  paginatedResponse,
  optionalCuid,
  optionalDateTime,
  optionalMoney,
  decimalString,
} from './common';
import { materialListItem } from './materials';

/**
 * Schemas for /projects and the nested /projects/:id/assignments sub-resource.
 * Mirrored on client and server via @kreart/shared.
 *
 * Financial fields (`financials`) are present on responses ONLY when the caller
 * has permission `PROJECTS_READ_FINANCIALS`. The shape is therefore optional on
 * the wire — the API strips them at DTO time, never the UI.
 */

/**
 * IN_WARRANTY is the one status the app sets on its own: the operations
 * scheduler stamps it when a production run finishes every stage and the
 * project lands in "Proiecte în garanție". Like every other status it stays
 * hand-settable — the transition model is permissive.
 *
 * ARCHIVED is the exception to that permissiveness. It is the "Arhivă" section,
 * where a project lands when its 24 months of warranty run out, and an archived
 * project is READ-ONLY: the API refuses every write on it except the one that
 * takes it back to IN_WARRANTY. The pair moves both ways by hand, and one way
 * on its own.
 */
export const projectStatusEnum = z.enum([
  'IN_PROGRESS',
  'ON_HOLD',
  'SENT_TO_CLIENT',
  'REOFFER',
  'ACCEPTED',
  'IN_WARRANTY',
  'ARCHIVED',
  'REJECTED',
]);
export type ProjectStatusValue = z.infer<typeof projectStatusEnum>;

export const projectCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type ProjectCurrencyValue = z.infer<typeof projectCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const projectFinancials = z.object({
  currency: projectCurrencyEnum,
  vatRate: decimalString,
  totalNet: decimalString,
  totalVat: decimalString,
  totalGross: decimalString,
  /**
   * Manual pricing — hand-entered on the project form for jobs that never get
   * an itemised offer. null means "not entered", which 0.00 could not express.
   *
   * These are a record kept ALONGSIDE the totals above, never a substitute for
   * them: totalNet stays derived from the priced items, so the two can be
   * compared rather than one quietly winning.
   */
  manualMaterialCost: decimalString.nullable(),
  manualOfferedPrice: decimalString.nullable(),
});
export type ProjectFinancials = z.infer<typeof projectFinancials>;

export const projectAssignmentDto = z.object({
  id: z.string().cuid(),
  userId: z.string().cuid(),
  fullName: z.string(),
  email: z.string(),
  assignedAt: z.string().datetime(),
});
export type ProjectAssignmentDto = z.infer<typeof projectAssignmentDto>;

export const projectListItem = z.object({
  id: z.string().cuid(),
  code: z.string(),
  title: z.string(),
  status: projectStatusEnum,
  clientId: z.string().cuid(),
  clientName: z.string(),
  deliveryAddressId: z.string().cuid().nullable(),
  agentId: z.string().cuid().nullable(),
  /**
   * Who opened the project. Immutable — `createdById` is never part of an
   * update request, so the name shown next to a project always points at the
   * person who actually created it, not whoever edited it last.
   */
  createdById: z.string().cuid(),
  createdByFullName: z.string(),
  /** When the project entered "Arhivă"; null unless it is ARCHIVED. */
  archivedAt: z.string().datetime().nullable(),
  deadline: z.string().datetime().nullable(),
  reofferCount: z.number().int().nonnegative(),
  assignmentCount: z.number().int().nonnegative(),
  roomCount: z.number().int().nonnegative(),
  /**
   * Whether the project has a run in the operations scheduler — waiting, in
   * production or finished under warranty. Such a project cannot be merged into
   * another: its stages and logged hours belong to that run and cannot follow a
   * copy, so the list says so before anyone picks it.
   */
  inScheduler: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
  financials: projectFinancials.optional(),
});
export type ProjectListItem = z.infer<typeof projectListItem>;

export const projectDetails = projectListItem
  .omit({ assignmentCount: true, roomCount: true, inScheduler: true })
  .extend({
    notes: z.string().nullable(),
    rejectionDetails: z.string().nullable(),
    statusChangedAt: z.string().datetime().nullable(),
    assignments: z.array(projectAssignmentDto),
  });
export type ProjectDetails = z.infer<typeof projectDetails>;

/**
 * How long a deleted project stays listed under "Proiecte șterse".
 *
 * The window is a LISTING rule, not a retention one: after it passes the
 * project simply stops appearing there. It stays soft-deleted in the database,
 * so nothing is destroyed by the clock running out — only an explicit
 * "șterge definitiv" does that.
 */
export const PROJECT_TRASH_DAYS = 7;

/** When a project deleted at `deletedAt` drops out of "Proiecte șterse". */
export function projectTrashExpiresAt(deletedAt: string | Date): Date {
  const at = new Date(deletedAt);
  return new Date(at.getTime() + PROJECT_TRASH_DAYS * 24 * 60 * 60 * 1000);
}

/**
 * Which slice of the projects list to read.
 *
 * The three are disjoint on purpose — a project belongs to exactly one, so no
 * row is listed twice and the sections can be counted independently.
 *
 * - ACTIVE: the everyday list. Excludes archived and deleted projects.
 * - ARCHIVED: the "Arhivă" section — read-only projects whose warranty ran out.
 * - DELETED: "Proiecte șterse" — deleted within the last PROJECT_TRASH_DAYS
 *   days. ADMIN only.
 */
export const projectsViewEnum = z.enum(['ACTIVE', 'ARCHIVED', 'DELETED']);
export type ProjectsView = z.infer<typeof projectsViewEnum>;

export const listProjectsQuery = z.object({
  view: projectsViewEnum.default('ACTIVE'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(100).optional(),
  status: z
    .union([projectStatusEnum, z.array(projectStatusEnum)])
    .transform((v) => (Array.isArray(v) ? v : [v]))
    .optional(),
  clientId: z.string().cuid().optional(),
  agentId: z
    .union([z.string().cuid(), z.array(z.string().cuid())])
    .transform((v) => (Array.isArray(v) ? v : [v]))
    .optional(),
  year: z
    .union([
      z.coerce.number().int().min(2000).max(2100),
      z.array(z.coerce.number().int().min(2000).max(2100)),
    ])
    .transform((v) => (Array.isArray(v) ? v : [v]))
    .optional(),
  month: z
    .union([
      z.coerce.number().int().min(1).max(12),
      z.array(z.coerce.number().int().min(1).max(12)),
    ])
    .transform((v) => (Array.isArray(v) ? v : [v]))
    .optional(),
  upcomingDeadline: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .optional(),
  assignedToMe: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => (typeof v === 'string' ? v === 'true' : v))
    .default(false),
});
export type ListProjectsQuery = z.infer<typeof listProjectsQuery>;

export const listProjectsResponse = paginatedResponse(projectListItem);
export type ListProjectsResponse = z.infer<typeof listProjectsResponse>;

export const createProjectRequest = z.object({
  clientId: z.string().cuid(),
  title: z.string().trim().min(1).max(200),
  deliveryAddressId: optionalCuid(),
  agentId: optionalCuid(),
  status: projectStatusEnum.optional(),
  deadline: optionalDateTime(),
  notes: optionalTrim(2000),
  /** Manual pricing; omit for a project that will be priced from its items. */
  manualMaterialCost: optionalMoney(),
  manualOfferedPrice: optionalMoney(),
});
export type CreateProjectRequest = z.infer<typeof createProjectRequest>;

/**
 * Create an independent copy of an existing project. Carries the same header
 * fields as a plain create — the dialog pre-fills them from the source, so the
 * copy can be retitled or pointed at another client on the way out — minus
 * `status`: a copy always starts IN_PROGRESS. It is a fresh quote, and
 * inheriting ACCEPTED would book an unsold job into analytics while inheriting
 * ARCHIVED would hand back a project that is read-only on arrival.
 *
 * What the server copies on top of these fields (rooms, areas, every line item
 * and the tender settings) is not expressible here and is not a client choice:
 * see `copyProjectContents` in the API.
 */
/**
 * One material-for-material replacement inside a PIECES cabinet's cut list.
 * Pieces carry their own materials, so a per-slot override has nothing to grab
 * onto there — the copy replaces `fromMaterialId` wherever that cabinet's
 * pieces use it.
 */
export const duplicatePieceMaterialSwap = z.object({
  fromMaterialId: z.string().cuid(),
  toMaterialId: z.string().cuid(),
});

/**
 * Per-row material overrides applied WHILE copying. Each `id` names a row of
 * the SOURCE project (the dialog lists the source; the copy does not exist
 * yet); a slot left out means "keep the source material". Rows the source no
 * longer has are skipped rather than failing the copy — the dialog may be a
 * few minutes stale.
 *
 * Every slot that IS overridden also gets its stored per-use markup refreshed
 * from the new board's catalog coefficient, exactly as if the material had
 * been picked in the item's own dialog.
 */
export const duplicateProjectMaterialOverrides = z.object({
  cabinets: z
    .array(
      z.object({
        id: z.string().cuid(),
        /** Carcasă. */
        materialId: z.string().cuid().optional(),
        /** Fronturi. */
        frontMaterialId: z.string().cuid().optional(),
        /** Spate. */
        backMaterialId: z.string().cuid().optional(),
        pieceMaterials: z.array(duplicatePieceMaterialSwap).max(50).optional(),
      }),
    )
    .max(500)
    .optional(),
  drawers: z
    .array(
      z.object({
        id: z.string().cuid(),
        /** Cutia sertarului. */
        materialId: z.string().cuid().optional(),
        /** Frontul sertarului. */
        frontMaterialId: z.string().cuid().optional(),
      }),
    )
    .max(1000)
    .optional(),
  panels: z
    .array(
      z.object({
        id: z.string().cuid(),
        materialId: z.string().cuid().optional(),
        frontMaterialId: z.string().cuid().optional(),
      }),
    )
    .max(500)
    .optional(),
});
export type DuplicateProjectMaterialOverrides = z.infer<typeof duplicateProjectMaterialOverrides>;

export const duplicateProjectRequest = createProjectRequest.omit({ status: true }).extend({
  /** Omitted = a faithful copy, byte for byte the same materials as the source. */
  materialOverrides: duplicateProjectMaterialOverrides.optional(),
});
export type DuplicateProjectRequest = z.infer<typeof duplicateProjectRequest>;

/** The most projects one merge takes: it copies row by row inside one transaction. */
export const MERGE_PROJECTS_MAX = 20;

/**
 * "Comprimă proiecte": bring several projects together into one new project.
 *
 * `projectIds` is in the order they were picked, which is the order their rooms
 * land in. The client and title are the only header fields the user sets; the
 * rest follows from the sources (see `ProjectsService.merge`). The sources go to
 * "Proiecte șterse" in the same transaction.
 */
export const mergeProjectsRequest = createProjectRequest
  .pick({ clientId: true, title: true })
  .extend({
    projectIds: z
      .array(z.string().cuid())
      .min(2)
      .max(MERGE_PROJECTS_MAX)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: 'A project can only be merged once.',
      }),
  });
export type MergeProjectsRequest = z.infer<typeof mergeProjectsRequest>;

/**
 * What the clone dialog's "Materiale" section lists: every cabinet, drawer and
 * panel of the source project reduced to its material slots, plus a label row
 * for each referenced material (including soft-deleted ones, which can still be
 * displayed but no longer picked as replacements).
 */
export const projectMaterialUsageMaterial = materialListItem.pick({
  id: true,
  name: true,
  sku: true,
  structure: true,
  thicknessMm: true,
  category: true,
});

export const projectMaterialUsage = z.object({
  cabinets: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      roomName: z.string().nullable(),
      constructionMode: z.enum(['PARAMETRIC', 'PIECES']),
      materialId: z.string().nullable(),
      frontMaterialId: z.string().nullable(),
      backMaterialId: z.string().nullable(),
      /** Distinct boards used by the cut list; empty unless PIECES. */
      pieceMaterialIds: z.array(z.string()),
    }),
  ),
  drawers: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      cabinetId: z.string(),
      cabinetName: z.string(),
      materialId: z.string().nullable(),
      frontMaterialId: z.string().nullable(),
    }),
  ),
  panels: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      roomName: z.string().nullable(),
      materialId: z.string().nullable(),
      frontMaterialId: z.string().nullable(),
    }),
  ),
  materials: z.array(projectMaterialUsageMaterial),
});
export type ProjectMaterialUsage = z.infer<typeof projectMaterialUsage>;
export type ProjectMaterialUsageMaterial = z.infer<typeof projectMaterialUsageMaterial>;

/**
 * Status changes go through the dedicated /transition endpoint (state machine).
 * Currency and vatRate are NOT editable here — once items are added, totals
 * depend on them; a future settings endpoint will enforce the right invariants.
 */
export const updateProjectRequest = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    clientId: optionalCuid(),
    deliveryAddressId: optionalCuid().or(z.null()),
    agentId: optionalCuid().or(z.null()),
    deadline: z.string().datetime().nullable().optional(),
    notes: optionalTrim(2000).or(z.null()),
    /** null clears the figure back to "not entered"; omitted leaves it alone. */
    manualMaterialCost: optionalMoney().or(z.null()),
    manualOfferedPrice: optionalMoney().or(z.null()),
  })
  .refine(
    (v) =>
      v.title !== undefined ||
      v.clientId !== undefined ||
      v.deliveryAddressId !== undefined ||
      v.agentId !== undefined ||
      v.deadline !== undefined ||
      v.notes !== undefined ||
      v.manualMaterialCost !== undefined ||
      v.manualOfferedPrice !== undefined,
    { message: 'At least one field is required.' },
  );
export type UpdateProjectRequest = z.infer<typeof updateProjectRequest>;

export const transitionProjectStatusRequest = z
  .object({
    status: projectStatusEnum,
    rejectionDetails: optionalTrim(1000),
  })
  .refine(
    (v) => v.status !== 'REJECTED' || (v.rejectionDetails && v.rejectionDetails.length >= 5),
    { message: 'Rejection requires details (min 5 chars).', path: ['rejectionDetails'] },
  );
export type TransitionProjectStatusRequest = z.infer<typeof transitionProjectStatusRequest>;

export const assignWorkerRequest = z.object({
  userId: z.string().cuid(),
});
export type AssignWorkerRequest = z.infer<typeof assignWorkerRequest>;
